import React, { useState, useEffect, useCallback } from 'react';
import {
  Package, Link2, Plus, Edit, Trash2, X, Save, FileText,
  Download, Eye, DollarSign, Building, RotateCcw, RotateCw, Lock,
  Share2, RefreshCw, CheckCircle, AlertCircle, Layers
} from 'lucide-react';
import { apiCall } from './Api';
import { PageLayout, PageHeader, PageBody } from './PageLayout';
import InvoiceTemplateDesigner from './InvoiceTemplateDesigner';

/*
 * CCAN Billing v2 — owner-scoped Service / Billing UI (session S4).
 *
 * The billing owner is derived server-side from the logged-in session
 * (admin tenant -> platform billing; host tenant -> that host's billing).
 * There is NO host picker: every endpoint here is already scoped to the
 * caller's owner. Chatbot is not special-cased anywhere — it appears only
 * as an ordinary service row (service_code === 'CHATBOT') in the catalog,
 * and its routes surface under Route Pricing once discovered.
 *
 * All calls target the frozen §4 API contract (base /api/billing):
 *   GET  /masters
 *   GET|POST|PUT /catalog     DELETE /catalog/{code}
 *   GET|POST /subscriptions   PUT|DELETE /subscriptions/{id}
 *   GET|POST|DELETE /pricing/customer
 *   GET  /routes            POST|DELETE /pricing/route
 *   GET|POST /buyers  GET /buyers/candidates  DELETE /buyers/{id}
 *   POST /usage/preview     POST /invoices/generate
 *   GET  /invoices  GET /invoices/{id}  (list/detail — unchanged, now owner-scoped)
 *   POST /invoices/{id}/undo|freeze|redo   GET /invoices/{id}/lifecycle
 */

const BASE = '/api/billing';
// Meta WhatsApp pricing categories — keep in sync with backend chatbot_usage.CATEGORIES.
const WA_CATEGORIES = ['authentication', 'marketing', 'service', 'utility'];

// ---- small fetch helpers -------------------------------------------------
const getJson = async (path) => {
  const res = await apiCall(path);
  if (!res.ok) return null;
  return res.json().catch(() => null);
};

const sendJson = async (path, method, body) => {
  const res = await apiCall(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body != null ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
};

// catalog rows carry label_override / service_name (not label / display_name)
const svcLabel = (c) => (c && (c.label_override || c.label || c.display_name || c.service_name)) || (c && c.service_code) || '';

const confirmDelete = (what) => window.confirm(`Remove ${what}? This cannot be undone from here.`);

const delJson = (path) => sendJson(path, 'DELETE');

// =========================================================================
// Root
// =========================================================================
const ServiceManagement = ({ user, section = 'billing' }) => {
  const isAdmin = user?.role === 'admin';
  const ownerLabel = isAdmin ? 'Platform (admin)' : `Host: ${user?.host_tenant_id || 'this tenant'}`;

  const serviceTabs = [
    { id: 'catalog', label: 'Service Catalog', icon: Package },
    { id: 'subscriptions', label: 'Subscriptions', icon: Link2 },
  ];
  const billingTabs = [
    { id: 'buyers', label: 'Buyers', icon: Building },
    { id: 'customer-pricing', label: 'Customer Pricing', icon: DollarSign },
    { id: 'route-pricing', label: 'Route Pricing', icon: Share2 },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'profile', label: 'Seller Profile', icon: Edit },
    { id: 'templates', label: 'Invoice Templates', icon: FileText },
  ];
  const tabs = section === 'services' ? serviceTabs : billingTabs;

  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const [loading, setLoading] = useState(false);

  // The Service and Billing sections have disjoint subtab sets, so when the
  // top-level section flips the previously active subtab belongs to the other
  // section — snap to this section's default (first) subtab instead of leaving
  // the stale view rendered. serviceTabs/billingTabs are stable inline literals,
  // so this depends only on `section`.
  useEffect(() => {
    const sectionTabs = section === 'services' ? serviceTabs : billingTabs;
    setActiveTab((prev) =>
      sectionTabs.some((t) => t.id === prev) ? prev : sectionTabs[0].id
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const [masters, setMasters] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const loadMasters = useCallback(async () => {
    const d = await getJson(`${BASE}/masters`);
    setMasters((d && (d.masters || d)) || []);
  }, []);
  const loadCatalog = useCallback(async () => {
    const d = await getJson(`${BASE}/catalog`);
    setCatalog((d && (d.catalog || d)) || []);
  }, []);
  const loadBuyers = useCallback(async () => {
    const d = await getJson(`${BASE}/buyers`);
    setBuyers((d && (d.buyers || d)) || []);
  }, []);
  const loadSubscriptions = useCallback(async () => {
    const d = await getJson(`${BASE}/subscriptions`);
    setSubscriptions((d && (d.subscriptions || d)) || []);
  }, []);
  const loadInvoices = useCallback(async () => {
    const d = await getJson(`${BASE}/invoices`);
    setInvoices((d && (d.invoices || d)) || []);
  }, []);

  // Catalog + buyers are referenced by nearly every tab (pricing dropdowns,
  // invoice generation, subscription forms), so keep them warm.
  useEffect(() => {
    loadCatalog();
    loadBuyers();
  }, [loadCatalog, loadBuyers]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        if (activeTab === 'catalog') {
          await Promise.all([loadMasters(), loadCatalog()]);
        } else if (activeTab === 'subscriptions') {
          await Promise.all([loadSubscriptions(), loadCatalog(), loadBuyers()]);
        } else if (activeTab === 'buyers') {
          await loadBuyers();
        } else if (activeTab === 'invoices') {
          await Promise.all([loadInvoices(), loadCatalog(), loadBuyers()]);
        }
      } catch (e) {
        console.error('Error loading billing data:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeTab, loadMasters, loadCatalog, loadBuyers, loadSubscriptions, loadInvoices]);

  return (
    <PageLayout>
      <PageHeader
        title={section === 'services' ? 'Service Management' : 'Billing'}
        icon={Building}
        actions={
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium border border-blue-100">
            <Building className="h-4 w-4" /> {ownerLabel}
          </span>
        }
      />
      <PageBody padded={false} className="p-6">

      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        {activeTab === 'catalog' && (
          <CatalogTab
            masters={masters}
            catalog={catalog}
            buyers={buyers}
            loading={loading}
            isAdmin={isAdmin}
            onRefresh={() => Promise.all([loadMasters(), loadCatalog()])}
          />
        )}
        {activeTab === 'subscriptions' && (
          <SubscriptionsTab
            subscriptions={subscriptions}
            catalog={catalog}
            buyers={buyers}
            loading={loading}
            onRefresh={loadSubscriptions}
          />
        )}
        {activeTab === 'buyers' && (
          <BuyersTab
            buyers={buyers}
            isAdmin={isAdmin}
            loading={loading}
            onRefresh={loadBuyers}
          />
        )}
        {activeTab === 'customer-pricing' && (
          <CustomerPricingTab
            catalog={catalog}
            buyers={buyers}
          />
        )}
        {activeTab === 'route-pricing' && (
          <RoutePricingTab
            catalog={catalog}
          />
        )}
        {activeTab === 'profile' && (isAdmin
          ? <div className="p-6"><h2 className="text-lg font-semibold text-gray-900">Platform seller profile</h2>
              <p className="text-sm text-gray-500 mb-4">The platform's details as the seller on invoices to hosts.</p>
              <PartyProfileForm partyId="self" /></div>
          : <SellerProfileTab />)}
        {activeTab === 'templates' && <InvoiceTemplateDesigner />}
        {activeTab === 'invoices' && (
          <InvoicesTab
            invoices={invoices}
            catalog={catalog}
            buyers={buyers}
            loading={loading}
            onRefresh={loadInvoices}
          />
        )}
      </div>
      </PageBody>
    </PageLayout>
  );
};

// =========================================================================
// Service Catalog — global masters + per-owner adoption
// =========================================================================
const CatalogTab = ({ masters, catalog, buyers = [], loading, isAdmin, onRefresh }) => {
  const [editing, setEditing] = useState(null); // service_code being label-edited
  const [labelDraft, setLabelDraft] = useState('');
  const [busy, setBusy] = useState('');

  const adopted = new Set(catalog.map((c) => c.service_code));
  const catByCode = {};
  catalog.forEach((c) => { catByCode[c.service_code] = c; });

  const adopt = async (code) => {
    setBusy(code);
    try {
      const r = await sendJson(`${BASE}/catalog`, 'POST', { service_code: code });
      if (!r.ok) { alert((r.data && r.data.detail) || 'Could not adopt this service'); return; }
      await onRefresh();
    } finally { setBusy(''); }
  };

  const updateCatalog = async (code, patch) => {
    setBusy(code);
    try {
      const cur = catByCode[code] || {};
      const body = {
        service_code: code,
        enabled: patch.enabled != null ? patch.enabled : (cur.enabled != null ? cur.enabled : true),
        label: patch.label != null ? patch.label : (cur.label_override || ''),
      };
      const r = await sendJson(`${BASE}/catalog`, 'PUT', body);
      if (!r.ok) { alert((r.data && r.data.detail) || 'Could not update the catalog entry'); return; }
      setEditing(null);
      await onRefresh();
    } finally { setBusy(''); }
  };

  const removeService = async (code) => {
    if (!confirmDelete(`${code} from your catalog (pricing is kept; re-adopt to restore)`)) return;
    setBusy(code);
    try {
      const r = await delJson(`${BASE}/catalog/${encodeURIComponent(code)}`);
      if (!r.ok) { alert((r.data && r.data.detail) || 'Could not remove this service'); return; }
      await onRefresh();
    } finally { setBusy(''); }
  };

  const canAdopt = (m) => isAdmin || m.tenant_exposable;

  // Platform admin: add / edit / retire global service masters.
  const blankMaster = { service_code: '', name: '', category: 'other', tenant_exposable: false, usage_source: 'subscription', default_unit: 'unit', platform_billing: 'usage' };
  const [masterForm, setMasterForm] = useState(null);   // null | { ...fields, editing: bool }
  const saveMaster = async () => {
    const f = masterForm;
    const body = { name: f.name, category: f.category, tenant_exposable: f.tenant_exposable, usage_source: f.usage_source, default_unit: f.default_unit, platform_billing: f.platform_billing || 'usage' };
    const r = f.editing
      ? await sendJson(`${BASE}/masters/${encodeURIComponent(f.service_code)}`, 'PUT', body)
      : await sendJson(`${BASE}/masters`, 'POST', { ...body, service_code: f.service_code });
    if (!r.ok) { alert((r.data && r.data.detail) || 'Could not save master'); return; }
    setMasterForm(null);
    await onRefresh();
  };
  const removeMaster = async (m) => {
    if (!confirmDelete(`master ${m.service_code}`)) return;
    const r = await delJson(`${BASE}/masters/${encodeURIComponent(m.service_code)}`);
    if (!r.ok) { alert((r.data && r.data.detail) || 'Could not remove master'); return; }
    await onRefresh();
  };

  return (
    <div className="p-6 space-y-8">
      {/* Global masters */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Global service masters</h2>
            <p className="text-sm text-gray-500">
              Feature definitions shared across the platform. Adopt a master to add it to your catalog.
            </p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <button onClick={() => setMasterForm({ ...blankMaster, editing: false })} className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                <Plus className="h-4 w-4" /> Add master
              </button>
            )}
            <button onClick={onRefresh} className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
          </div>
        </div>
        {masterForm && (
          <div className="mb-4 border border-blue-200 bg-blue-50 rounded-lg p-4">
            <div className="grid grid-cols-7 gap-3 items-end">
              <Field label="Code *">
                <input value={masterForm.service_code} readOnly={masterForm.editing} onChange={(e) => setMasterForm({ ...masterForm, service_code: e.target.value.toUpperCase() })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm read-only:bg-gray-100" placeholder="WHATSAPP_API" />
              </Field>
              <Field label="Name *">
                <input value={masterForm.name} onChange={(e) => setMasterForm({ ...masterForm, name: e.target.value })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
              </Field>
              <Field label="Category">
                <select value={masterForm.category} onChange={(e) => setMasterForm({ ...masterForm, category: e.target.value })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm">
                  {['messaging', 'analytics', 'url_shortener', 'cdp', 'chatbot', 'backup', 'disaster_recovery', 'high_availability', 'storage', 'other'].map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Usage source">
                <select value={masterForm.usage_source} onChange={(e) => setMasterForm({ ...masterForm, usage_source: e.target.value })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm">
                  <option value="subscription">subscription (flat)</option>
                  <option value="metered">metered (CDR)</option>
                  <option value="cdr">cdr</option>
                  <option value="chatbot">chatbot messages</option>
                </select>
              </Field>
              <Field label="Unit">
                <input value={masterForm.default_unit} onChange={(e) => setMasterForm({ ...masterForm, default_unit: e.target.value })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
              </Field>
              <Field label="Platform bills host">
                <select value={masterForm.platform_billing || 'usage'} onChange={(e) => setMasterForm({ ...masterForm, platform_billing: e.target.value })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm">
                  <option value="usage">per usage</option>
                  <option value="subscription">product subscription</option>
                </select>
              </Field>
              <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                <input type="checkbox" checked={masterForm.tenant_exposable} onChange={(e) => setMasterForm({ ...masterForm, tenant_exposable: e.target.checked })} className="w-4 h-4" />
                Tenant-exposable
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setMasterForm(null)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white">Cancel</button>
              <button onClick={saveMaster} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg">{masterForm.editing ? 'Save' : 'Create'}</button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="text-center py-8"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto" /></div>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Service</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Category</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Usage source</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Exposure</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {masters.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No masters available</td></tr>
                ) : masters.map((m) => (
                  <tr key={m.service_code}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{m.name || m.service_code}</div>
                      <div className="text-xs text-gray-400">{m.service_code}</div>
                    </td>
                    <td className="px-4 py-2">{m.category || '—'}</td>
                    <td className="px-4 py-2">{m.usage_source || '—'}</td>
                    <td className="px-4 py-2">
                      {m.tenant_exposable ? (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-800">Tenant-exposable</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">Platform-only</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {isAdmin && (
                        <span className="inline-flex gap-2 mr-3 align-middle">
                          <button onClick={() => setMasterForm({ ...blankMaster, ...m, editing: true })} className="text-blue-600 hover:text-blue-800" title="Edit master"><Edit className="h-4 w-4" /></button>
                          <button onClick={() => removeMaster(m)} className="text-red-600 hover:text-red-800" title="Retire master"><Trash2 className="h-4 w-4" /></button>
                        </span>
                      )}
                      {adopted.has(m.service_code) ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                          <CheckCircle className="h-4 w-4" /> Adopted
                        </span>
                      ) : canAdopt(m) ? (
                        <button
                          onClick={() => adopt(m.service_code)}
                          disabled={busy === m.service_code}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          <Plus className="h-3.5 w-3.5" /> Adopt
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">Not exposable</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Adopted catalog */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Your catalog</h2>
        <p className="text-sm text-gray-500 mb-4">
          Services you have adopted. Toggle availability or override the display label.
        </p>
        {catalog.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-gray-200 rounded-lg text-gray-400">
            No services adopted yet — adopt one from the masters above.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {catalog.map((c) => {
              const master = masters.find((m) => m.service_code === c.service_code) || {};
              const enabled = c.enabled != null ? c.enabled : true;
              const label = c.label_override || master.name || svcLabel(c);
              return (
                <div key={c.service_code} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      {editing === c.service_code ? (
                        <input
                          autoFocus
                          value={labelDraft}
                          onChange={(e) => setLabelDraft(e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Display label"
                        />
                      ) : (
                        <h3 className="font-semibold text-gray-900 truncate">{label}</h3>
                      )}
                      <p className="text-xs text-gray-400 mt-1">{c.service_code}</p>
                    </div>
                    {editing === c.service_code ? (
                      <div className="flex gap-1 ml-2">
                        <button onClick={() => updateCatalog(c.service_code, { label: labelDraft })} className="text-emerald-600 hover:text-emerald-800" title="Save label"><Save className="h-4 w-4" /></button>
                        <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-600" title="Cancel"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <div className="flex gap-2 ml-2">
                        <button onClick={() => { setEditing(c.service_code); setLabelDraft(label); }} className="text-blue-600 hover:text-blue-800" title="Edit label"><Edit className="h-4 w-4" /></button>
                        <button onClick={() => removeService(c.service_code)} disabled={busy === c.service_code} className="text-red-600 hover:text-red-800 disabled:opacity-50" title="Remove service"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-3 text-sm">
                    <span className="text-gray-500">{master.category || '—'}</span>
                    <button
                      onClick={() => updateCatalog(c.service_code, { enabled: !enabled })}
                      disabled={busy === c.service_code}
                      className={`px-2 py-1 text-xs rounded-full ${enabled ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} disabled:opacity-50`}
                    >
                      {enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add a custom service — first-class at BOTH owner levels (S7). A custom is
          private to this owner, live immediately, and never surfaced to the platform
          by default. Master adoption above stays adopt-only + exposure-filtered. */}
      <CustomServiceForm onRefresh={onRefresh} />

      {/* Manual usage / line-item entry for metered · one-time · volume customs. */}
      <ManualUsagePanel catalog={catalog} buyers={buyers} />

      {/* Admin-only, on-demand: what tenants offer (never shown by default). */}
      {isAdmin && <AdminCustomReport />}
    </div>
  );
};

// -------------------------------------------------------------------------
// Add custom service (S7) — name, category, unit, billing style + optional pricing.
// POST /catalog/custom (owner derived server-side; admin AND host).
// -------------------------------------------------------------------------
const CUSTOM_STYLES = [
  { id: 'subscription', label: 'Subscription / flat (recurring)', pricing: 'flat' },
  { id: 'one_time', label: 'One-time charge', pricing: 'flat' },
  { id: 'volume', label: 'Volume-based (slabs)', pricing: 'slabs' },
  { id: 'metered', label: 'Metered / manual', pricing: 'slabs' },
];
const CATEGORY_OPTS = ['other', 'messaging', 'analytics', 'url_shortener', 'cdp', 'backup', 'disaster_recovery', 'storage'];

const CustomServiceForm = ({ onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({
    name: '', category: 'other', unit: 'unit', billing_style: 'subscription',
    currency: 'INR', flat_fee: '', slabs: [{ slab_name: 'Tier 1', slab_order: 1, min_units: 0, max_units: 0, price_per_unit: 0 }],
  });
  const style = CUSTOM_STYLES.find((s) => s.id === form.billing_style) || CUSTOM_STYLES[0];

  const setSlab = (i, patch) => setForm({ ...form, slabs: form.slabs.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  const addSlab = () => setForm({ ...form, slabs: [...form.slabs, { slab_name: `Tier ${form.slabs.length + 1}`, slab_order: form.slabs.length + 1, min_units: 0, max_units: 0, price_per_unit: 0 }] });
  const removeSlab = (i) => setForm({ ...form, slabs: form.slabs.filter((_, idx) => idx !== i) });

  const submit = async () => {
    if (!form.name.trim()) { setMsg('A service name is required'); return; }
    setBusy(true); setMsg('');
    try {
      const body = {
        name: form.name.trim(), billing_style: form.billing_style,
        category: form.category, unit: form.unit || 'unit', currency: form.currency,
      };
      if (style.pricing === 'flat') {
        if (form.flat_fee !== '') body.flat_fee = Number(form.flat_fee);
      } else {
        body.slabs = form.slabs.map((s, idx) => ({
          slab_name: s.slab_name || `Tier ${idx + 1}`,
          slab_order: Number(s.slab_order) || idx + 1,
          min_units: Number(s.min_units) || 0,
          max_units: Number(s.max_units) || 0,
          price_per_unit: Number(s.price_per_unit) || 0,
          currency: form.currency,
        }));
      }
      const r = await sendJson(`${BASE}/catalog/custom`, 'POST', body);
      if (!r.ok) { setMsg((r.data && r.data.detail) || 'Could not create the custom service'); return; }
      setMsg(`Created ${r.data.service_code}`);
      setForm({ ...form, name: '', flat_fee: '' });
      setOpen(false);
      if (onRefresh) await onRefresh();
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-dashed border-blue-200 rounded-lg p-5 bg-blue-50/40">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-500" /> Add a custom service
          </h2>
          <p className="text-sm text-gray-500">
            Define your own service beyond the masters — private to you, live immediately. Any billing style.
          </p>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="h-4 w-4" /> {open ? 'Close' : 'New custom service'}
        </button>
      </div>

      {open && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. Integration FTE / Bespoke Export" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Billing style</label>
              <select value={form.billing_style} onChange={(e) => setForm({ ...form, billing_style: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {CUSTOM_STYLES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {CATEGORY_OPTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
              <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="unit / hour / export / month" />
            </div>
          </div>

          {style.pricing === 'flat' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{form.billing_style === 'one_time' ? 'One-time fee' : 'Recurring fee'} ({form.currency})</label>
                <input type="number" value={form.flat_fee} onChange={(e) => setForm({ ...form, flat_fee: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. 1000" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
                <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-gray-600">Per-unit slabs ({form.currency})</label>
                <button onClick={addSlab} className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add slab</button>
              </div>
              <div className="space-y-2">
                {form.slabs.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input value={s.slab_name} onChange={(e) => setSlab(i, { slab_name: e.target.value })} className="col-span-3 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="Tier" />
                    <input type="number" value={s.min_units} onChange={(e) => setSlab(i, { min_units: e.target.value })} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="min" />
                    <input type="number" value={s.max_units} onChange={(e) => setSlab(i, { max_units: e.target.value })} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="max (0=∞)" />
                    <input type="number" value={s.price_per_unit} onChange={(e) => setSlab(i, { price_per_unit: e.target.value })} className="col-span-3 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="price/unit" />
                    <button onClick={() => removeSlab(i)} className="col-span-2 text-gray-400 hover:text-red-500 justify-self-start"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
              <Save className="h-4 w-4" /> Create service
            </button>
            {msg && <span className="text-sm text-gray-600 inline-flex items-center gap-1"><AlertCircle className="h-4 w-4 text-gray-400" /> {msg}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------------
// Manual usage / line-item entry (S7) — metered · one-time · volume customs.
// POST /usage/manual. Only offered for custom, manual-style services.
// -------------------------------------------------------------------------
const MANUAL_MODELS = ['metered', 'one_time', 'volume_based'];

const ManualUsagePanel = ({ catalog = [], buyers = [] }) => {
  const manualServices = catalog.filter(
    (c) => c.master_conformant === false && MANUAL_MODELS.includes(c.pricing_model)
  );
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ service_code: '', buyer_id: '', usage_date: today, quantity: 1, description: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!form.service_code && manualServices.length) setForm((f) => ({ ...f, service_code: manualServices[0].service_code }));
  }, [manualServices, form.service_code]);

  const submit = async () => {
    if (!form.service_code) { setMsg('Pick a service'); return; }
    if (!form.buyer_id) { setMsg('Pick a buyer'); return; }
    setBusy(true); setMsg('');
    try {
      const body = {
        service_code: form.service_code, buyer_id: form.buyer_id, usage_date: form.usage_date,
        quantity: Number(form.quantity) || 0, description: form.description || null,
      };
      const r = await sendJson(`${BASE}/usage/manual`, 'POST', body);
      if (!r.ok) { setMsg((r.data && r.data.detail) || 'Could not record the entry'); return; }
      setMsg(`Recorded ${form.quantity} × ${form.service_code}`);
      setForm({ ...form, quantity: 1, description: '' });
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-5">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <FileText className="h-5 w-5 text-gray-400" /> Manual usage / line item
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Record usage for a metered, one-time or volume custom service. It is priced through your slabs at invoice time.
      </p>
      {manualServices.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-gray-200 rounded-lg text-gray-400 text-sm">
          No metered / one-time / volume custom services yet — create one above.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Service</label>
            <select value={form.service_code} onChange={(e) => setForm({ ...form, service_code: e.target.value })} className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm">
              {manualServices.map((c) => <option key={c.service_code} value={c.service_code}>{c.label_override || c.service_name || c.service_code}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Buyer</label>
            <select value={form.buyer_id} onChange={(e) => setForm({ ...form, buyer_id: e.target.value })} className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="">Select…</option>
              {buyers.map((b) => <option key={b.buyer_id} value={b.buyer_id}>{b.display_name || b.buyer_id}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
            <input type="date" value={form.usage_date} onChange={(e) => setForm({ ...form, usage_date: e.target.value })} className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Quantity</label>
            <input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <button onClick={submit} disabled={busy} className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              <Plus className="h-4 w-4" /> Record
            </button>
          </div>
          <div className="md:col-span-5">
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Description (optional) — e.g. 12 consulting hours, week of…" />
          </div>
          {msg && <div className="md:col-span-5 text-sm text-gray-600 inline-flex items-center gap-1"><CheckCircle className="h-4 w-4 text-emerald-500" /> {msg}</div>}
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------------
// Admin-only, on-demand report of tenant custom services (S7, decision 7).
// GET /admin/custom-services — the ONLY cross-owner view; never shown by default.
// -------------------------------------------------------------------------
const AdminCustomReport = () => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getJson(`${BASE}/admin/custom-services`);
      setRows((d && d.custom_services) || []);
    } finally { setLoading(false); }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Building className="h-5 w-5 text-gray-400" /> Tenant custom services (on-demand)
          </h2>
          <p className="text-sm text-gray-500">
            What tenants offer. Private by default — the platform sees this only when it asks.
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
          <RefreshCw className="h-4 w-4" /> {rows === null ? 'Load report' : 'Refresh'}
        </button>
      </div>
      {loading ? (
        <div className="text-center py-6"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" /></div>
      ) : rows === null ? null : rows.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-sm mt-3">No tenant custom services.</div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg mt-4">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Owner</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Service</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Style</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Unit</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500">Pricing</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500">Entries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((r) => (
                <tr key={`${r.billing_owner}:${r.service_code}`}>
                  <td className="px-4 py-2 font-medium text-gray-900">{r.billing_owner}</td>
                  <td className="px-4 py-2">
                    <div>{r.label_override || r.service_name}</div>
                    <div className="text-xs text-gray-400">{r.service_code}</div>
                  </td>
                  <td className="px-4 py-2">{r.pricing_model}</td>
                  <td className="px-4 py-2">{r.unit_name}</td>
                  <td className="px-4 py-2 text-right">{r.pricing_rows}</td>
                  <td className="px-4 py-2 text-right">{r.manual_entries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// =========================================================================
// Subscriptions — buyer -> service (owner-scoped)
// =========================================================================
const SubscriptionsTab = ({ subscriptions, catalog, buyers, loading, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ buyer_id: '', service_code: '', include_in_billing: true });
  const [busy, setBusy] = useState(false);

  const buyerName = (id) => {
    const b = buyers.find((x) => String(x.buyer_id) === String(id));
    return b ? (b.display_name || b.buyer_id) : id;
  };
  const serviceName = (code) => {
    const c = catalog.find((x) => x.service_code === code);
    return c ? (svcLabel(c)) : code;
  };

  const submit = async () => {
    if (!form.buyer_id || !form.service_code) { alert('Choose a buyer and a service'); return; }
    setBusy(true);
    try {
      const r = await sendJson(`${BASE}/subscriptions`, 'POST', {
        buyer_id: form.buyer_id,
        service_code: form.service_code,
        include_in_billing: form.include_in_billing,
      });
      if (!r.ok) { alert((r.data && r.data.detail) || 'Could not save subscription'); return; }
      setShowForm(false);
      setForm({ buyer_id: '', service_code: '', include_in_billing: true });
      onRefresh();
    } finally { setBusy(false); }
  };

  const updateSub = async (s, patch) => {
    const r = await sendJson(`${BASE}/subscriptions/${s.id}`, 'PUT', patch);
    if (!r.ok) { alert((r.data && r.data.detail) || 'Could not update subscription'); return; }
    onRefresh();
  };
  // Suspend / cancel stop ALL traffic for the buyer on the service at once — the
  // user must type DISABLE_WORD (the server enforces the same word).
  const DISABLE_WORD = 'DISABLE';
  const [disabling, setDisabling] = useState(null);   // { sub, action: 'suspend'|'cancel' }
  const [confirmText, setConfirmText] = useState('');
  const askDisable = (sub, action) => { setConfirmText(''); setDisabling({ sub, action }); };
  const doDisable = async () => {
    const { sub, action } = disabling;
    const r = action === 'cancel'
      ? await delJson(`${BASE}/subscriptions/${sub.id}?confirm=${encodeURIComponent(confirmText)}`)
      : await sendJson(`${BASE}/subscriptions/${sub.id}`, 'PUT', { subscription_status: 'suspended', confirm: confirmText });
    if (!r.ok) { alert((r.data && r.data.detail) || 'Could not update subscription'); return; }
    setDisabling(null);
    onRefresh();
  };
  const impact = (code) => (code === 'CHATBOT'
    ? 'All chatbot messages in and out'
    : code === 'CDP' ? 'All CDP campaign execution' : 'All usage and operations');
  const statusBadge = (st) => ({
    active: 'bg-green-100 text-green-800', trial: 'bg-blue-100 text-blue-800',
    suspended: 'bg-amber-100 text-amber-800', cancelled: 'bg-gray-100 text-gray-700',
  }[st] || 'bg-gray-100 text-gray-700');

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Subscriptions</h2>
        <button onClick={() => setShowForm(true)} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="h-4 w-4" /><span>Add Subscription</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto" /></div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Buyer</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Service</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Since</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">In billing</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Status</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {subscriptions.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No subscriptions yet</td></tr>
              ) : subscriptions.map((s) => (
                <tr key={s.id || `${s.buyer_id}:${s.service_code}`}>
                  <td className="px-4 py-2 font-medium">{buyerName(s.buyer_id)}</td>
                  <td className="px-4 py-2">{serviceName(s.service_code)}</td>
                  <td className="px-4 py-2 text-gray-600">{s.start_date || '—'}</td>
                  <td className="px-4 py-2">
                    <button onClick={() => updateSub(s, { include_in_billing: s.include_in_billing === false })} title="Toggle include in billing">
                      {s.include_in_billing !== false ? (
                        <CheckCircle className="h-4 w-4 text-emerald-600" />
                      ) : (
                        <X className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${statusBadge(s.subscription_status)}`}>
                      {s.subscription_status || 'active'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {s.subscription_status === 'suspended' ? (
                      <button onClick={() => updateSub(s, { subscription_status: 'active' })} className="text-xs text-blue-600 hover:text-blue-800 mr-3">Resume</button>
                    ) : (
                      <button onClick={() => askDisable(s, 'suspend')} className="text-xs text-amber-600 hover:text-amber-800 mr-3">Suspend</button>
                    )}
                    <button onClick={() => askDisable(s, 'cancel')} className="text-red-600 hover:text-red-800 align-middle" title="Cancel subscription"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {disabling && (
        <Modal title={`${disabling.action === 'cancel' ? 'Cancel' : 'Suspend'} subscription`} onClose={() => setDisabling(null)}>
          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">{impact(disabling.sub.service_code)} for {buyerName(disabling.sub.buyer_id)} will stop with immediate effect.</p>
                <p className="mt-1">
                  {disabling.sub.service_code === 'CHATBOT'
                    ? 'Incoming messages are dropped and no replies, campaigns or agent messages are sent for every workflow billed to this buyer. '
                    : ''}
                  {disabling.action === 'cancel'
                    ? 'Cancelling ends the subscription today; re-subscribe to restore service.'
                    : 'Resume the subscription to restore service.'}
                </p>
              </div>
            </div>
            <Field label={`Type ${DISABLE_WORD} to confirm`}>
              <input autoFocus value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder={DISABLE_WORD} />
            </Field>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDisabling(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Keep running</button>
              <button onClick={doDisable} disabled={confirmText !== DISABLE_WORD} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {disabling.action === 'cancel' ? 'Cancel subscription' : 'Suspend now'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showForm && (
        <Modal title="Add Subscription" onClose={() => setShowForm(false)}>
          <div className="space-y-4">
            <Field label="Buyer *">
              <select value={form.buyer_id} onChange={(e) => setForm({ ...form, buyer_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select buyer…</option>
                {buyers.map((b) => <option key={b.buyer_id} value={b.buyer_id}>{b.display_name || b.buyer_id} ({b.kind})</option>)}
              </select>
            </Field>
            <Field label="Service *">
              <select value={form.service_code} onChange={(e) => setForm({ ...form, service_code: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select service…</option>
                {catalog.map((c) => <option key={c.service_code} value={c.service_code}>{svcLabel(c)}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.include_in_billing} onChange={(e) => setForm({ ...form, include_in_billing: e.target.checked })} className="w-4 h-4 text-blue-600 rounded" />
              Include in billing invoices
            </label>
            <FormButtons onCancel={() => setShowForm(false)} onSave={submit} busy={busy} saveLabel="Create" />
          </div>
        </Modal>
      )}
    </div>
  );
};

// =========================================================================
// Buyers — unified buyer table (host | customer | reseller, nestable)
// =========================================================================
const BuyersTab = ({ buyers, isAdmin, loading, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);   // buyer_id being edited (id locked)
  const [candidates, setCandidates] = useState([]);   // hosts (admin) / customers (host) from masters
  const [pick, setPick] = useState('');               // '' | candidate buyer_id | '__manual__'
  const [detailsFor, setDetailsFor] = useState(null); // buyer whose billing details are open
  const [wfs, setWfs] = useState([]);                 // host: chatbot workflow -> buyer attribution
  const WF_URL = '/api/billing/chatbot/config/my/workflow-customers';
  const loadWfs = useCallback(async () => {
    if (isAdmin) return;
    const d = await getJson(WF_URL);
    setWfs((d && d.workflows) || []);
  }, [isAdmin]);
  useEffect(() => { loadWfs(); }, [loadWfs]);
  const assignWf = async (workflowId, customerId) => {
    const r = await sendJson(WF_URL, 'PUT', { workflow_id: workflowId, customer_id: customerId || null });
    if (!r.ok) { alert((r.data && r.data.detail) || 'Could not update workflow billing'); }
    loadWfs();
  };
  const blank = {
    buyer_id: '', kind: isAdmin ? 'host' : 'customer', parent_buyer_id: '', display_name: '',
    currency: 'INR', timezone: 'UTC', country_code: '', state_code: '',
    tax_registered: false, source: 'portal', external_ref: '', is_active: true,
  };
  const [form, setForm] = useState(blank);

  const openAdd = async () => {
    setEditingId(null); setPick(''); setForm(blank); setShowForm(true);
    const d = await getJson(`${BASE}/buyers/candidates`);
    setCandidates((d && d.candidates) || []);
  };
  const openEdit = (b) => {
    setEditingId(b.buyer_id); setPick('__manual__');
    const f = {};
    Object.keys(blank).forEach((k) => { f[k] = b[k] != null ? b[k] : blank[k]; });
    setForm(f); setShowForm(true);
  };
  const choose = (id) => {
    setPick(id);
    if (id === '__manual__' || id === '') { setForm({ ...blank }); return; }
    const c = candidates.find((x) => x.buyer_id === id) || {};
    setForm({
      ...blank, buyer_id: c.buyer_id, display_name: c.display_name || c.buyer_id, kind: c.kind || blank.kind,
      currency: c.currency || blank.currency, timezone: c.timezone || blank.timezone,
      source: 'onboarding', external_ref: c.buyer_id,
    });
  };
  const remove = async (b) => {
    if (!confirmDelete(`buyer ${b.display_name || b.buyer_id}`)) return;
    const r = await delJson(`${BASE}/buyers/${encodeURIComponent(b.buyer_id)}`);
    if (!r.ok) { alert((r.data && r.data.detail) || 'Could not remove buyer'); return; }
    onRefresh();
  };

  const submit = async () => {
    if (!form.buyer_id || !form.display_name) { alert('Buyer id and display name are required'); return; }
    setBusy(true);
    try {
      const body = { ...form, parent_buyer_id: form.parent_buyer_id || null };
      const r = await sendJson(`${BASE}/buyers`, 'POST', body);
      if (!r.ok) { alert((r.data && r.data.detail) || 'Could not save buyer'); return; }
      setShowForm(false);
      setForm(blank);
      onRefresh();
    } finally { setBusy(false); }
  };

  const kindBadge = (k) => {
    const map = { host: 'bg-indigo-100 text-indigo-800', customer: 'bg-blue-100 text-blue-800', reseller: 'bg-purple-100 text-purple-800' };
    return map[k] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Buyers</h2>
          <p className="text-sm text-gray-500">Who you bill. Nest resellers/customers with a parent buyer.</p>
        </div>
        <button onClick={openAdd} className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="h-4 w-4" /><span>Add Buyer</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto" /></div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Buyer</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Kind</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Parent</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Currency</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Region</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Source</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Status</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {buyers.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No buyers yet</td></tr>
              ) : buyers.map((b) => (
                <tr key={b.buyer_id}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900">{b.display_name || b.buyer_id}</div>
                    <div className="text-xs text-gray-400">{b.buyer_id}</div>
                  </td>
                  <td className="px-4 py-2"><span className={`px-2 py-0.5 text-xs rounded-full ${kindBadge(b.kind)}`}>{b.kind}</span></td>
                  <td className="px-4 py-2 text-gray-600">{b.parent_buyer_id || '—'}</td>
                  <td className="px-4 py-2">{b.currency || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{[b.country_code, b.state_code].filter(Boolean).join(' / ') || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{b.source || '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${b.is_active === false ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-800'}`}>
                      {b.is_active === false ? 'Inactive' : 'Active'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {!isAdmin && <button onClick={() => setDetailsFor(b)} className="text-gray-600 hover:text-gray-900 mr-3" title="Billing details (address, GSTIN)"><FileText className="h-4 w-4" /></button>}
                    <button onClick={() => openEdit(b)} className="text-blue-600 hover:text-blue-800 mr-3" title="Edit buyer"><Edit className="h-4 w-4" /></button>
                    <button onClick={() => remove(b)} className="text-red-600 hover:text-red-800" title="Remove buyer"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailsFor && (
        <Modal title={`Billing details — ${detailsFor.display_name || detailsFor.buyer_id}`} onClose={() => setDetailsFor(null)} wide>
          <PartyProfileForm partyId={detailsFor.buyer_id} onDone={() => setDetailsFor(null)} />
        </Modal>
      )}

      {!isAdmin && (
        <div className="mt-8">
          <h3 className="text-base font-semibold text-gray-900">Chatbot workflow billing</h3>
          <p className="text-sm text-gray-500 mb-3">Which buyer each chatbot workflow's messages are billed to. Unassigned usage is billed to UNASSIGNED.</p>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Workflow</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Billed to</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {wfs.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400">No chatbot workflows</td></tr>
                ) : wfs.map((w) => (
                  <tr key={w.workflow_id}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{w.workflow_name}</div>
                      <div className="text-xs text-gray-400">{w.workflow_id}</div>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{w.status}</td>
                    <td className="px-4 py-2">
                      <select value={w.customer_id || ''} onChange={(e) => assignWf(w.workflow_id, e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-64">
                        <option value="">Unassigned</option>
                        {buyers.filter((b) => b.kind !== 'host').map((b) => <option key={b.buyer_id} value={b.buyer_id}>{b.display_name || b.buyer_id}</option>)}
                        {w.customer_id && !buyers.some((b) => b.buyer_id === w.customer_id) && <option value={w.customer_id}>{w.customer_name || w.customer_id} (not a buyer)</option>}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <Modal title={editingId ? `Edit Buyer — ${editingId}` : 'Add Buyer'} onClose={() => setShowForm(false)}>
          <div className="space-y-4">
            {!editingId && (
              <Field label={isAdmin ? 'Host' : 'Customer'}>
                <select value={pick} onChange={(e) => choose(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">{isAdmin ? 'Select a host…' : 'Select one of your customers…'}</option>
                  {candidates.map((c) => (
                    <option key={c.buyer_id} value={c.buyer_id} disabled={c.registered}>
                      {c.display_name} ({c.buyer_id}){c.registered ? ' — already a buyer' : ''}
                    </option>
                  ))}
                  <option value="__manual__">{isAdmin ? 'Other host — enter manually' : 'New customer (not from SMSC) — enter manually'}</option>
                </select>
              </Field>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Buyer id *">
                <input value={form.buyer_id} onChange={(e) => setForm({ ...form, buyer_id: e.target.value })} readOnly={!!editingId || (pick !== '' && pick !== '__manual__')} className="w-full px-3 py-2 border border-gray-300 rounded-lg read-only:bg-gray-50" placeholder={isAdmin ? 'host id' : 'customer id'} />
              </Field>
              <Field label="Display name *">
                <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Kind">
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="customer">Customer</option>
                  {isAdmin && <option value="host">Host</option>}
                  <option value="reseller">Reseller</option>
                </select>
              </Field>
              <Field label="Parent buyer (nesting)">
                <select value={form.parent_buyer_id} onChange={(e) => setForm({ ...form, parent_buyer_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">None (top level)</option>
                  {buyers.filter((b) => b.buyer_id !== form.buyer_id).map((b) => <option key={b.buyer_id} value={b.buyer_id}>{b.display_name || b.buyer_id}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Currency">
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option><option>AED</option>
                </select>
              </Field>
              <Field label="Timezone">
                <input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g. Asia/Kolkata" />
              </Field>
              <Field label="Source">
                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="portal">Portal</option>
                  <option value="onboarding">Onboarding</option>
                  <option value="etl">ETL</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Country code">
                <input value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value.toUpperCase() })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="IN" />
              </Field>
              <Field label="State code">
                <input value={form.state_code} onChange={(e) => setForm({ ...form, state_code: e.target.value.toUpperCase() })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="MH" />
              </Field>
              <Field label="External ref">
                <input value={form.external_ref} onChange={(e) => setForm({ ...form, external_ref: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </Field>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.tax_registered} onChange={(e) => setForm({ ...form, tax_registered: e.target.checked })} className="w-4 h-4 text-blue-600 rounded" />
                Tax registered
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 text-blue-600 rounded" />
                Active
              </label>
            </div>
            <FormButtons onCancel={() => setShowForm(false)} onSave={submit} busy={busy} saveLabel={editingId ? 'Save' : 'Create'} />
          </div>
        </Modal>
      )}
    </div>
  );
};

// =========================================================================
// Customer Pricing — per (service, buyer|default) slabs + optional block
// =========================================================================
const emptySlab = () => ({ slab_order: 1, min_units: 0, max_units: 999999999, price_per_unit: 0, flat_fee: 0 });

const CustomerPricingTab = ({ catalog, buyers }) => {
  const [service, setService] = useState('');
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [routeOptions, setRouteOptions] = useState([]); // discovered routes for the service
  const [form, setForm] = useState({ buyer_id: '', route_key: '', currency: 'INR', block_price: '', slabs: [] });

  const load = useCallback(async (code) => {
    setLoading(true);
    try {
      const q = code ? `?service_code=${encodeURIComponent(code)}` : '';
      const d = await getJson(`${BASE}/pricing/customer${q}`);
      setPricing((d && (d.pricing || d.slabs)) || []);
    } finally { setLoading(false); }
  }, []);

  // U1: discovered routes (channel:direction) for the selected service, so a host
  // can price a route (e.g. whatsapp:sent) differently from another (whatsapp:received).
  const loadRoutes = useCallback(async (code) => {
    if (!code) { setRouteOptions([]); return; }
    try {
      const d = await getJson(`${BASE}/routes?service_code=${encodeURIComponent(code)}`);
      const rs = (d && (d.routes || [])) || [];
      const keys = rs.map((r) => (typeof r === 'string' ? r : r.route_key)).filter(Boolean);
      // WhatsApp channels are also priced per Meta category ('<channel>:marketing' …),
      // read by the chatbot usage report. Discovery (frozen contract) only lists sent/received.
      const waChannels = [...new Set(keys.map((k) => k.split(':')[0]).filter((c) => c.startsWith('whatsapp')))];
      const catKeys = waChannels.flatMap((c) => WA_CATEGORIES.map((cat) => `${c}:${cat}`));
      setRouteOptions([...keys, ...catKeys.filter((k) => !keys.includes(k))]);
    } catch { setRouteOptions([]); }
  }, []);

  useEffect(() => {
    if (!service && catalog.length) setService(catalog[0].service_code);
  }, [catalog, service]);

  useEffect(() => { if (service) { load(service); loadRoutes(service); } }, [service, load, loadRoutes]);

  const buyerName = (id) => {
    if (!id) return 'Default (all buyers)';
    const b = buyers.find((x) => String(x.buyer_id) === String(id));
    return b ? (b.display_name || b.buyer_id) : id;
  };

  const setSlab = (i, patch) => {
    const slabs = form.slabs.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    setForm({ ...form, slabs });
  };
  const addSlab = () => setForm({ ...form, slabs: [...form.slabs, { ...emptySlab(), slab_order: form.slabs.length + 1 }] });
  const removeSlab = (i) => setForm({ ...form, slabs: form.slabs.filter((_, idx) => idx !== i) });

  const openEdit = (p) => {
    setForm({
      buyer_id: p.buyer_id || '', route_key: p.route_key || '', currency: p.currency || 'INR',
      block_price: p.block_price != null ? String(p.block_price) : '',
      slabs: (p.slabs || []).map((s) => ({
        slab_order: s.slab_order, min_units: s.min_units, max_units: s.max_units,
        price_per_unit: s.price_per_unit, flat_fee: s.flat_fee || 0,
      })),
    });
    setShowForm(true);
  };
  const removePricing = async (p) => {
    if (!confirmDelete(`pricing for ${buyerName(p.buyer_id)}${p.route_key ? ` · ${p.route_key}` : ''}`)) return;
    const q = new URLSearchParams({ service_code: p.service_code, route_key: p.route_key || '' });
    if (p.buyer_id) q.set('buyer_id', p.buyer_id);
    const r = await delJson(`${BASE}/pricing/customer?${q.toString()}`);
    if (!r.ok) { alert((r.data && r.data.detail) || 'Could not remove pricing'); return; }
    load(service);
  };

  const submit = async () => {
    if (!service) { alert('Select a service'); return; }
    if (form.slabs.length === 0 && form.block_price === '') { alert('Add at least one slab or a block price'); return; }
    setBusy(true);
    try {
      const body = {
        service_code: service,
        buyer_id: form.buyer_id || null,
        route_key: form.route_key || '',
        currency: form.currency,
        block_price: form.block_price === '' ? null : Number(form.block_price),
        slabs: form.slabs.map((s, idx) => ({
          slab_order: Number(s.slab_order) || idx + 1,
          min_units: Number(s.min_units) || 0,
          max_units: Number(s.max_units) || 0,
          price_per_unit: Number(s.price_per_unit) || 0,
          flat_fee: Number(s.flat_fee) || 0,
        })),
      };
      const r = await sendJson(`${BASE}/pricing/customer`, 'POST', body);
      if (!r.ok) { alert((r.data && r.data.detail) || 'Could not save pricing'); return; }
      setShowForm(false);
      setForm({ buyer_id: '', route_key: '', currency: 'INR', block_price: '', slabs: [] });
      load(service);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Customer Pricing</h2>
          <p className="text-sm text-gray-500">Slab pricing per service; leave the buyer empty for a default that applies to all buyers. Block pricing is optional.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Service</label>
            <select value={service} onChange={(e) => setService(e.target.value)} className="border border-gray-300 rounded-md px-3 py-2 text-sm w-56">
              {catalog.length === 0 && <option value="">No services adopted</option>}
              {catalog.map((c) => <option key={c.service_code} value={c.service_code}>{svcLabel(c)}</option>)}
            </select>
          </div>
          <button onClick={() => { setForm({ buyer_id: '', route_key: '', currency: 'INR', block_price: '', slabs: [] }); setShowForm(true); }} disabled={!service} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            <Plus className="h-4 w-4" /><span>Add Pricing</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto" /></div>
      ) : pricing.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-gray-200 rounded-lg text-gray-400">No customer pricing for this service yet</div>
      ) : (
        <div className="space-y-4">
          {pricing.map((p, i) => (
            <div key={p.id || `${p.service_code}:${p.buyer_id || 'default'}:${i}`} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{buyerName(p.buyer_id)}{p.route_key ? <span className="ml-2 px-2 py-0.5 text-[11px] rounded bg-indigo-50 text-indigo-700 align-middle">{p.route_key}</span> : null}</h3>
                  <p className="text-xs text-gray-400">{p.service_code} · {p.currency || form.currency}{p.route_key ? '' : ' · whole service'}{p.block_price != null && p.block_price !== '' ? ` · block ${Number(p.block_price).toFixed(2)}/unit` : ''}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => openEdit(p)} className="text-blue-600 hover:text-blue-800" title="Edit pricing"><Edit className="h-4 w-4" /></button>
                  <button onClick={() => removePricing(p)} className="text-red-600 hover:text-red-800" title="Remove pricing"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="space-y-2">
                {(p.slabs || []).length === 0 ? (
                  <p className="text-sm text-gray-400">{p.block_price != null ? 'No slabs — block price applies to all units' : 'No slabs'}</p>
                ) : (p.slabs || []).map((s, idx) => (
                  <div key={s.id || idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                    <span className="text-gray-600">{s.min_units} – {Number(s.max_units) >= 999999999 ? '∞' : s.max_units} units</span>
                    <span className="font-medium">{p.currency || ''} {Number(s.price_per_unit).toFixed(2)}/unit{Number(s.flat_fee) > 0 ? ` + ${Number(s.flat_fee).toFixed(2)} flat` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <Modal title={`Customer Pricing — ${service}`} onClose={() => setShowForm(false)} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <Field label="Buyer">
                <select value={form.buyer_id} onChange={(e) => setForm({ ...form, buyer_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">Default (all buyers)</option>
                  {buyers.map((b) => <option key={b.buyer_id} value={b.buyer_id}>{b.display_name || b.buyer_id}</option>)}
                </select>
              </Field>
              <Field label="Route / direction">
                <select value={form.route_key} onChange={(e) => setForm({ ...form, route_key: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">Whole service (default)</option>
                  {routeOptions.map((rk) => <option key={rk} value={rk}>{rk}</option>)}
                </select>
              </Field>
              <Field label="Currency">
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option><option>AED</option>
                </select>
              </Field>
              <Field label="Block price / unit (optional)">
                <input type="number" step="0.01" value={form.block_price} onChange={(e) => setForm({ ...form, block_price: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="per unit, when no slab matches" />
              </Field>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Slabs</label>
                <button onClick={addSlab} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"><Plus className="h-3.5 w-3.5" /> Add slab</button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Tiered pricing by units used in the period. <span className="font-medium">Order</span> ranks the tiers;
                <span className="font-medium"> Min</span>/<span className="font-medium">Max</span> are the unit band (Max 999999999 = unlimited);
                <span className="font-medium"> Price/unit</span> applies within the band; <span className="font-medium">Flat fee</span> is an optional one-off added when the band is used.
                Slabs are optional — units no slab covers are charged at the <span className="font-medium">Block price</span>.
              </p>
              {form.slabs.length > 0 && <div className="grid grid-cols-12 gap-2 items-center px-1 mb-1">
                <span className="col-span-2 text-[11px] font-medium text-gray-500 uppercase tracking-wide">Order</span>
                <span className="col-span-2 text-[11px] font-medium text-gray-500 uppercase tracking-wide">Min units</span>
                <span className="col-span-3 text-[11px] font-medium text-gray-500 uppercase tracking-wide">Max units</span>
                <span className="col-span-2 text-[11px] font-medium text-gray-500 uppercase tracking-wide">Price/unit</span>
                <span className="col-span-2 text-[11px] font-medium text-gray-500 uppercase tracking-wide">Flat fee</span>
                <span className="col-span-1" />
              </div>}
              <div className="space-y-2">
                {form.slabs.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input type="number" aria-label="Slab order" value={s.slab_order} onChange={(e) => setSlab(i, { slab_order: e.target.value })} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="order" />
                    <input type="number" aria-label="Minimum units" value={s.min_units} onChange={(e) => setSlab(i, { min_units: e.target.value })} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="min" />
                    <input type="number" aria-label="Maximum units (999999999 = unlimited)" value={s.max_units} onChange={(e) => setSlab(i, { max_units: e.target.value })} className="col-span-3 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="max (999999999 = ∞)" />
                    <input type="number" step="0.01" aria-label="Price per unit" value={s.price_per_unit} onChange={(e) => setSlab(i, { price_per_unit: e.target.value })} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="price/unit" />
                    <input type="number" step="0.01" aria-label="Flat fee" value={s.flat_fee} onChange={(e) => setSlab(i, { flat_fee: e.target.value })} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="flat" />
                    <button onClick={() => removeSlab(i)} className="col-span-1 text-red-600 hover:text-red-800 flex justify-center" aria-label="Remove slab"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            <FormButtons onCancel={() => setShowForm(false)} onSave={submit} busy={busy} saveLabel="Save Pricing" />
          </div>
        </Modal>
      )}
    </div>
  );
};

// =========================================================================
// Route Pricing — routes auto-discovered from deploy channels
// =========================================================================
const RoutePricingTab = ({ catalog }) => {
  const [service, setService] = useState('');
  const [routes, setRoutes] = useState([]);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({}); // route_key -> { block, currency, slabs[], open }
  const [busy, setBusy] = useState('');

  const load = useCallback(async (code) => {
    if (!code) return;
    setLoading(true);
    try {
      const d = await getJson(`${BASE}/routes?service_code=${encodeURIComponent(code)}`);
      const rs = (d && (d.routes || [])) || [];
      setRoutes(rs);
      setPending(d && d.pending ? d.pending : null);
      const nextDrafts = {};
      rs.forEach((r) => {
        nextDrafts[r.route_key] = {
          block: r.block_price != null ? String(r.block_price) : '',
          currency: r.currency || 'INR',
          slabs: (r.slabs || []).map((sl) => ({
            slab_order: sl.slab_order, min_units: sl.min_units, max_units: sl.max_units,
            price_per_unit: sl.price_per_unit, flat_fee: sl.flat_fee || 0,
          })),
          open: false,
        };
      });
      setDrafts(nextDrafts);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!service && catalog.length) setService(catalog[0].service_code);
  }, [catalog, service]);
  useEffect(() => { if (service) load(service); }, [service, load]);

  const setDraft = (key, patch) => setDrafts((d) => ({ ...d, [key]: { ...(d[key] || {}), ...patch } }));

  const setSlabAt = (key, i, patch) => setDrafts((d) => {
    const cur = d[key] || {};
    const slabs = (cur.slabs || []).map((sl, idx) => (idx === i ? { ...sl, ...patch } : sl));
    return { ...d, [key]: { ...cur, slabs } };
  });
  const addRouteSlab = (key) => setDrafts((d) => {
    const cur = d[key] || {};
    const slabs = [...(cur.slabs || []), { ...emptySlab(), slab_order: (cur.slabs || []).length + 1 }];
    return { ...d, [key]: { ...cur, slabs, open: true } };
  });
  const removeRouteSlab = (key, i) => setDrafts((d) => {
    const cur = d[key] || {};
    return { ...d, [key]: { ...cur, slabs: (cur.slabs || []).filter((_, idx) => idx !== i) } };
  });

  const savePrice = async (route) => {
    const draft = drafts[route.route_key] || {};
    const slabs = draft.slabs || [];
    if (slabs.length === 0 && (draft.block === '' || draft.block == null)) {
      alert('Enter a block price and/or add slabs'); return;
    }
    setBusy(route.route_key);
    try {
      const r = await sendJson(`${BASE}/pricing/route`, 'POST', {
        service_code: service,
        route_key: route.route_key,
        currency: draft.currency || 'INR',
        block_price: draft.block === '' || draft.block == null ? null : Number(draft.block),
        slabs: slabs.map((sl, idx) => ({
          slab_order: Number(sl.slab_order) || idx + 1,
          min_units: Number(sl.min_units) || 0,
          max_units: Number(sl.max_units) || 0,
          price_per_unit: Number(sl.price_per_unit) || 0,
          flat_fee: Number(sl.flat_fee) || 0,
        })),
      });
      if (r.status === 501) { alert('Route pricing is not available yet (backend pending — S3).'); return; }
      if (!r.ok) { alert((r.data && r.data.detail) || 'Could not save route price'); return; }
      load(service);
    } finally { setBusy(''); }
  };

  const clearPrice = async (route) => {
    if (!confirmDelete(`the price for ${route.route_key}`)) return;
    setBusy(route.route_key);
    try {
      const q = new URLSearchParams({ service_code: service, route_key: route.route_key });
      const r = await delJson(`${BASE}/pricing/route?${q.toString()}`);
      if (!r.ok) { alert((r.data && r.data.detail) || 'Could not clear route price'); return; }
      load(service);
    } finally { setBusy(''); }
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Route Pricing</h2>
          <p className="text-sm text-gray-500">Routes are auto-discovered from the service's deploy channels. Slabs are optional — units no slab covers are costed at the block price.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Service</label>
          <select value={service} onChange={(e) => setService(e.target.value)} className="border border-gray-300 rounded-md px-3 py-2 text-sm w-56">
            {catalog.length === 0 && <option value="">No services adopted</option>}
            {catalog.map((c) => <option key={c.service_code} value={c.service_code}>{svcLabel(c)}</option>)}
          </select>
        </div>
      </div>

      {pending && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 mt-0.5" />
          <span>Route discovery for this service is not live yet (backend stage: {String(pending)}). Routes will auto-populate once available.</span>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto" /></div>
      ) : routes.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-gray-200 rounded-lg text-gray-400">
          No routes discovered for this service.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Route</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Channel</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Direction</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Block price / unit</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Currency</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Slabs</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {routes.map((r) => {
                const parts = String(r.route_key || '').split(':');
                const draft = drafts[r.route_key] || {};
                return (
                  <React.Fragment key={r.route_key}>
                  <tr>
                    <td className="px-4 py-2 font-medium">
                      {r.route_key}
                      {r.priced_via && r.priced_via !== r.route_key && <div className="text-[11px] text-gray-400">inherited from {r.priced_via}</div>}
                    </td>
                    <td className="px-4 py-2">{r.channel || parts[0] || '—'}</td>
                    <td className="px-4 py-2">{r.direction || parts[1] || '—'}</td>
                    <td className="px-4 py-2">
                      <input type="number" step="0.0001" value={draft.block != null ? draft.block : ''} onChange={(e) => setDraft(r.route_key, { block: e.target.value })} className="w-28 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="optional" />
                    </td>
                    <td className="px-4 py-2">
                      <select value={draft.currency || 'INR'} onChange={(e) => setDraft(r.route_key, { currency: e.target.value })} className="px-2 py-1.5 border border-gray-300 rounded text-sm">
                        <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option><option>AED</option>
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <button onClick={() => setDraft(r.route_key, { open: !draft.open })} className="text-xs text-blue-600 hover:text-blue-800">
                        {(draft.slabs || []).length} slab{(draft.slabs || []).length === 1 ? '' : 's'} {draft.open ? '▲' : '▼'}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={() => savePrice(r)} disabled={busy === r.route_key} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                        <Save className="h-3.5 w-3.5" /> Save
                      </button>
                      {(r.own_slabs || r.own_block) && (
                        <button onClick={() => clearPrice(r)} disabled={busy === r.route_key} className="ml-2 text-red-600 hover:text-red-800 disabled:opacity-50 align-middle" title="Clear route pricing"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </td>
                  </tr>
                  {draft.open && (
                    <tr className="bg-gray-50">
                      <td colSpan={7} className="px-4 py-3">
                        <div className="grid grid-cols-12 gap-2 items-center px-1 mb-1">
                          <span className="col-span-2 text-[11px] font-medium text-gray-500 uppercase">Order</span>
                          <span className="col-span-2 text-[11px] font-medium text-gray-500 uppercase">Min units</span>
                          <span className="col-span-3 text-[11px] font-medium text-gray-500 uppercase">Max units</span>
                          <span className="col-span-2 text-[11px] font-medium text-gray-500 uppercase">Price/unit</span>
                          <span className="col-span-2 text-[11px] font-medium text-gray-500 uppercase">Flat fee</span>
                          <span className="col-span-1" />
                        </div>
                        <div className="space-y-2">
                          {(draft.slabs || []).map((sl, i) => (
                            <div key={i} className="grid grid-cols-12 gap-2 items-center">
                              <input type="number" value={sl.slab_order} onChange={(e) => setSlabAt(r.route_key, i, { slab_order: e.target.value })} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm" />
                              <input type="number" value={sl.min_units} onChange={(e) => setSlabAt(r.route_key, i, { min_units: e.target.value })} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm" />
                              <input type="number" value={sl.max_units} onChange={(e) => setSlabAt(r.route_key, i, { max_units: e.target.value })} className="col-span-3 px-2 py-1.5 border border-gray-300 rounded text-sm" placeholder="0 or 999999999 = ∞" />
                              <input type="number" step="0.0001" value={sl.price_per_unit} onChange={(e) => setSlabAt(r.route_key, i, { price_per_unit: e.target.value })} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm" />
                              <input type="number" step="0.01" value={sl.flat_fee} onChange={(e) => setSlabAt(r.route_key, i, { flat_fee: e.target.value })} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-sm" />
                              <button onClick={() => removeRouteSlab(r.route_key, i)} className="col-span-1 text-red-600 hover:text-red-800 flex justify-center" aria-label="Remove slab"><Trash2 className="h-4 w-4" /></button>
                            </div>
                          ))}
                        </div>
                        <button onClick={() => addRouteSlab(r.route_key)} className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"><Plus className="h-3.5 w-3.5" /> Add slab</button>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// =========================================================================
// Invoices — generic preview/generate + list + undo/redo/freeze
// =========================================================================
const InvoicesTab = ({ invoices, catalog, buyers, loading, onRefresh }) => {
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [lifecycle, setLifecycle] = useState(null);

  // generate panel
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';
  const [gen, setGen] = useState({ buyer_id: '', service_code: '', period_start: monthStart, period_end: today });
  const [preview, setPreview] = useState(null);
  const [genBusy, setGenBusy] = useState(false);

  const buyerName = (id) => {
    const b = buyers.find((x) => String(x.buyer_id) === String(id));
    return b ? (b.display_name || b.buyer_id) : id;
  };

  const statusColor = (status) => ({
    draft: 'bg-gray-100 text-gray-800',
    sent: 'bg-blue-100 text-blue-800',
    viewed: 'bg-purple-100 text-purple-800',
    partially_paid: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    overdue: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800',
  }[status] || 'bg-gray-100 text-gray-800');

  const runPreview = async () => {
    if (!gen.buyer_id || !gen.service_code) { alert('Choose a buyer and a service'); return; }
    setGenBusy(true);
    setPreview(null);
    try {
      const r = await sendJson(`${BASE}/usage/preview`, 'POST', {
        buyer_id: gen.buyer_id,
        service_code: gen.service_code,
        period_start: gen.period_start,
        period_end: gen.period_end,
      });
      if (!r.ok) { alert((r.data && r.data.detail) || 'Preview failed'); return; }
      setPreview(r.data);
    } finally { setGenBusy(false); }
  };

  const runGenerate = async () => {
    if (!gen.buyer_id || !gen.service_code) { alert('Choose a buyer and a service'); return; }
    if (!window.confirm('Generate an invoice for this buyer / service / period?')) return;
    setGenBusy(true);
    try {
      const r = await sendJson(`${BASE}/invoices/generate`, 'POST', {
        buyer_id: gen.buyer_id,
        service_code: gen.service_code,
        period_start: gen.period_start,
        period_end: gen.period_end,
      });
      if (!r.ok) { alert((r.data && r.data.detail) || 'Generation failed'); return; }
      setPreview(null);
      onRefresh();
    } finally { setGenBusy(false); }
  };

  const viewInvoice = async (inv) => {
    const d = await getJson(`${BASE}/invoices/${inv.id}`);
    setSelected((d && (d.invoice || d)) || inv);
    const lc = await getJson(`${BASE}/invoices/${inv.id}/lifecycle`);
    setLifecycle((lc && (lc.events || lc.lifecycle || lc)) || null);
    setShowModal(true);
  };

  const lifecycleAction = async (inv, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    const r = await sendJson(`${BASE}/invoices/${inv.id}/${action}`, 'POST');
    if (!r.ok) { alert((r.data && r.data.detail) || 'Action failed'); return; }
    onRefresh();
  };
  const blankPay = () => ({ payment_amount: '', payment_date: new Date().toISOString().slice(0, 10), payment_method: 'bank_transfer', transaction_id: '' });
  const [pay, setPay] = useState(blankPay());
  const recordPayment = async () => {
    if (!(Number(pay.payment_amount) > 0)) { alert('Enter a payment amount'); return; }
    const r = await sendJson(`${BASE}/payments`, 'POST', { ...pay, invoice_id: selected.id, payment_amount: Number(pay.payment_amount) });
    if (!r.ok) { alert((r.data && r.data.detail) || 'Could not record payment'); return; }
    setPay(blankPay());
    await viewInvoice(selected);
    onRefresh();
  };
  const handleUndo = (inv) => lifecycleAction(inv, 'undo', 'Undo this bill? It will be voided and its usage released back to unbilled.');
  const handleFreeze = (inv) => lifecycleAction(inv, 'freeze', 'Freeze this bill? It can no longer be undone or redone.');
  const handleRedo = (inv) => lifecycleAction(inv, 'redo', 'Redo this bill? The current invoice is voided and regenerated from current data.');

  const downloadPdf = async (inv) => {
    try {
      const res = await apiCall(`${BASE}/invoices/${inv.id}/pdf`, { method: 'GET' });
      if (!res.ok) { alert('Failed to download invoice PDF'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number || inv.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) { console.error('Error downloading invoice:', e); }
  };

  return (
    <div className="p-6">
      {/* Generate panel */}
      <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
        <h3 className="text-md font-semibold text-gray-900 mb-3">Generate invoice</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Buyer</label>
            <select value={gen.buyer_id} onChange={(e) => setGen({ ...gen, buyer_id: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm w-52">
              <option value="">Select buyer…</option>
              {buyers.map((b) => <option key={b.buyer_id} value={b.buyer_id}>{b.display_name || b.buyer_id}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Service</label>
            <select value={gen.service_code} onChange={(e) => setGen({ ...gen, service_code: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm w-52">
              <option value="">Select service…</option>
              {catalog.map((c) => <option key={c.service_code} value={c.service_code}>{svcLabel(c)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input type="date" value={gen.period_start} onChange={(e) => setGen({ ...gen, period_start: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input type="date" value={gen.period_end} onChange={(e) => setGen({ ...gen, period_end: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <button onClick={runPreview} disabled={genBusy} className="inline-flex items-center gap-1 px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-white disabled:opacity-50">
            <Eye className="h-4 w-4" /> Preview
          </button>
          <button onClick={runGenerate} disabled={genBusy} className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
            <FileText className="h-4 w-4" /> Generate
          </button>
        </div>

        {preview && (
          <div className="mt-4 text-sm">
            <div className="text-gray-700 mb-2">
              {preview.period && <span className="font-medium">{preview.period} · </span>}
              Estimated{' '}
              <span className="font-medium">
                {preview.estimated_subtotal != null ? preview.estimated_subtotal : (preview.estimated_subtotal_base != null ? preview.estimated_subtotal_base : '—')}{' '}
                {preview.currency || preview.base_currency || ''}
              </span>
              {preview.total_units != null && <> · units {preview.total_units}</>}
            </div>
            {Array.isArray(preview.lines || preview.rows) && (preview.lines || preview.rows).length > 0 && (
              <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-500">Route / description</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Units</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-500">Estimated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(preview.lines || preview.rows).map((ln, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2">{ln.route_key || ln.description || ln.service_code || '—'}</td>
                        <td className="px-4 py-2 text-right">{ln.units != null ? ln.units : (ln.quantity != null ? ln.quantity : '—')}</td>
                        <td className="px-4 py-2 text-right">{ln.estimated_cost != null ? ln.estimated_cost : (ln.line_total != null ? ln.line_total : '—')} {ln.currency || preview.currency || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Invoices</h2>
        <button onClick={onRefresh} className="inline-flex items-center gap-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Buyer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-gray-400">No invoices yet</td></tr>
              ) : invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{inv.invoice_number}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.buyer_name || buyerName(inv.buyer_id)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{inv.invoice_date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{inv.currency} {parseFloat(inv.total_amount || 0).toLocaleString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${statusColor(inv.invoice_status)}`}>{inv.invoice_status}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex space-x-2">
                      <button onClick={() => viewInvoice(inv)} className="text-blue-600 hover:text-blue-800" title="View"><Eye className="h-4 w-4" /></button>
                      <button onClick={() => downloadPdf(inv)} className="text-green-600 hover:text-green-800" title="Download PDF"><Download className="h-4 w-4" /></button>
                      {inv.is_frozen ? (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500" title="Frozen — locked"><Lock className="h-4 w-4" /> Frozen</span>
                      ) : inv.invoice_status !== 'cancelled' && (
                        <>
                          <button onClick={() => handleUndo(inv)} className="text-amber-600 hover:text-amber-800" title="Undo bill"><RotateCcw className="h-4 w-4" /></button>
                          <button onClick={() => handleRedo(inv)} className="text-indigo-600 hover:text-indigo-800" title="Redo bill (regenerate)"><RotateCw className="h-4 w-4" /></button>
                          <button onClick={() => handleFreeze(inv)} className="text-gray-700 hover:text-black" title="Freeze bill (lock)"><Lock className="h-4 w-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && selected && (
        <Modal title="Invoice Details" onClose={() => setShowModal(false)} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-gray-500">Invoice Number</p><p className="font-semibold">{selected.invoice_number}</p></div>
              <div><p className="text-sm text-gray-500">Status</p><span className={`px-2 py-1 text-xs rounded-full ${statusColor(selected.invoice_status)}`}>{selected.invoice_status}</span></div>
              <div><p className="text-sm text-gray-500">Buyer</p><p className="font-semibold">{buyerName(selected.buyer_id != null ? selected.buyer_id : (selected.customer_id != null ? selected.customer_id : selected.host_tenant_id))}</p></div>
              <div><p className="text-sm text-gray-500">Invoice Date</p><p className="font-semibold">{selected.invoice_date}</p></div>
            </div>

            <div>
              <h4 className="text-md font-semibold mb-3">Line Items</h4>
              <table className="min-w-full divide-y divide-gray-200 border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Quantity</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Unit Price</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y">
                  {(selected.line_items || []).map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2 text-sm">
                        <p className="font-medium">{item.service_name || item.service_code}</p>
                        <p className="text-gray-500">{item.description}</p>
                      </td>
                      <td className="px-4 py-2 text-sm text-right">{parseFloat(item.quantity || 0).toLocaleString()} {item.unit_name || ''}</td>
                      <td className="px-4 py-2 text-sm text-right">{selected.currency} {parseFloat(item.unit_price || 0).toLocaleString()}</td>
                      <td className="px-4 py-2 text-sm text-right font-medium">{selected.currency} {parseFloat(item.line_total || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end">
              <div className="w-80 space-y-2 text-sm">
                <div className="flex justify-between"><span>Subtotal:</span><span className="font-medium">{selected.currency} {parseFloat(selected.subtotal || 0).toLocaleString()}</span></div>
                <div className="flex justify-between text-base font-semibold border-t pt-2"><span>Total Amount:</span><span>{selected.currency} {parseFloat(selected.total_amount || 0).toLocaleString()}</span></div>
              </div>
            </div>

            <div>
              <h4 className="text-md font-semibold mb-2">Payments</h4>
              {(selected.payments || []).length === 0 ? (
                <p className="text-sm text-gray-400">No payments recorded</p>
              ) : (
                <ul className="text-sm text-gray-700 space-y-1">
                  {selected.payments.map((p) => (
                    <li key={p.id} className="flex justify-between">
                      <span>{p.payment_date} · {p.payment_method}{p.transaction_id ? ` · ${p.transaction_id}` : ''} <span className="text-gray-400">{p.payment_reference}</span></span>
                      <span className="font-medium">{selected.currency} {parseFloat(p.payment_amount || 0).toLocaleString('en-IN')}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex justify-between text-sm mt-2"><span>Amount due</span><span className="font-semibold">{selected.currency} {parseFloat(selected.amount_due || 0).toLocaleString('en-IN')}</span></div>
              {parseFloat(selected.amount_due || 0) > 0 && selected.invoice_status !== 'cancelled' && (
                <div className="grid grid-cols-5 gap-2 items-end mt-3">
                  <input type="number" step="0.01" value={pay.payment_amount} onChange={(e) => setPay({ ...pay, payment_amount: e.target.value })} placeholder="Amount" className="px-2 py-1.5 border border-gray-300 rounded text-sm" />
                  <input type="date" value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} className="px-2 py-1.5 border border-gray-300 rounded text-sm" />
                  <select value={pay.payment_method} onChange={(e) => setPay({ ...pay, payment_method: e.target.value })} className="px-2 py-1.5 border border-gray-300 rounded text-sm">
                    {['bank_transfer', 'upi', 'cheque', 'cash', 'credit_card', 'debit_card', 'other'].map((m) => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
                  </select>
                  <input value={pay.transaction_id} onChange={(e) => setPay({ ...pay, transaction_id: e.target.value })} placeholder="Txn / UTR ref" className="px-2 py-1.5 border border-gray-300 rounded text-sm" />
                  <button onClick={recordPayment} className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">Record payment</button>
                </div>
              )}
            </div>

            {Array.isArray(lifecycle) && lifecycle.length > 0 && (
              <div>
                <h4 className="text-md font-semibold mb-2">Lifecycle</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {lifecycle.map((ev, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                      <span className="font-medium">{ev.action || ev.event || ev.status}</span>
                      <span className="text-gray-400">{ev.at || ev.created_at || ev.timestamp || ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="border-t px-0 pt-4 mt-4 flex justify-end">
            <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// =========================================================================
// Party billing details (GET|PUT|DELETE /parties/{id}): a host's customer — used as the
// BILL TO on its invoices (SMSC customers pre-fill from the synced master) — or, for the
// platform, its own seller details (partyId = 'self').
// =========================================================================
const PARTY_FORM = [
  ['legal_name', 'Legal name *'], ['display_name', 'Display name'],
  ['address_line1', 'Address line 1'], ['address_line2', 'Address line 2'],
  ['city', 'City'], ['state', 'State'], ['state_code', 'GST state code'], ['postal_code', 'PIN code'],
  ['country', 'Country'], ['country_code', 'Country code'], ['gstin', 'GSTIN'], ['pan', 'PAN'],
  ['contact_name', 'Contact name'], ['contact_email', 'Contact email'], ['contact_phone', 'Contact phone'],
  ['finance_email', 'Finance email'], ['bank_name', 'Bank'], ['bank_account_number', 'Account number'],
  ['bank_ifsc_code', 'IFSC'],
];

const PartyProfileForm = ({ partyId, onDone }) => {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(false);
  const [source, setSource] = useState('');
  const [busy, setBusy] = useState(false);
  const url = `${BASE}/parties/${encodeURIComponent(partyId)}`;
  useEffect(() => {
    (async () => {
      const d = await getJson(url);
      const base = { country: 'India', country_code: 'IN' };
      if (d && d.profile) { setForm({ ...base, ...d.profile }); setSaved(true); }
      else if (d && d.prefill) { setForm({ ...base, ...d.prefill }); setSource('smsc'); }
      else setForm(base);
    })();
  }, [url]);
  const save = async () => {
    if (!(form.legal_name || '').trim()) { alert('Legal name is required'); return; }
    setBusy(true);
    try {
      const body = {};
      PARTY_FORM.forEach(([k]) => { body[k] = form[k] == null || form[k] === '' ? null : form[k]; });
      const r = await sendJson(url, 'PUT', body);
      if (!r.ok) { alert((r.data && r.data.detail) || 'Could not save billing details'); return; }
      setSaved(true); setSource('');
      if (onDone) onDone();
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!confirmDelete('these billing details')) return;
    const r = await delJson(url);
    if (!r.ok) { alert((r.data && r.data.detail) || 'Could not remove'); return; }
    if (onDone) onDone(); else { setForm({ country: 'India', country_code: 'IN' }); setSaved(false); }
  };
  if (!form) return <div className="text-gray-400">Loading…</div>;
  return (
    <div>
      {source === 'smsc' && <p className="text-xs text-blue-700 mb-3">Pre-filled from the SMSC customer record — complete and save.</p>}
      <div className="grid grid-cols-3 gap-4">
        {PARTY_FORM.map(([k, label]) => (
          <Field key={k} label={label}>
            <input value={form[k] == null ? '' : form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </Field>
        ))}
      </div>
      <div className="flex justify-end gap-3 mt-6">
        {saved && <button onClick={remove} className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50">Remove</button>}
        <button onClick={save} disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Save</button>
      </div>
    </div>
  );
};

// =========================================================================
// Seller Profile — the host's own details as the SELLER on invoices to its customers
// (GET|PUT /profile). Required before a host can generate invoices.
// =========================================================================
const PROFILE_FIELDS = [
  ['company_legal_name', 'Legal name *'], ['company_display_name', 'Display name *'],
  ['billing_address_line1', 'Address line 1 *'], ['billing_address_line2', 'Address line 2'],
  ['billing_city', 'City *'], ['billing_state', 'State *'], ['state_code', 'GST state code'],
  ['billing_postal_code', 'PIN code *'], ['billing_country', 'Country'],
  ['gstin', 'GSTIN'], ['pan', 'PAN'],
  ['billing_contact_name', 'Contact name *'], ['billing_contact_email', 'Contact email *'],
  ['billing_contact_phone', 'Contact phone *'], ['finance_contact_email', 'Finance email'],
  ['bank_name', 'Bank'], ['bank_account_number', 'Account number'], ['bank_ifsc_code', 'IFSC'],
];

const SellerProfileTab = () => {
  const blank = { billing_country: 'India', billing_currency: 'INR', payment_terms_days: 30, is_registered_under_gst: false };
  const [form, setForm] = useState(blank);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      const d = await getJson(`${BASE}/profile`);
      if (d && d.profile) setForm({ ...blank, ...d.profile });
      setLoaded(true);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      PROFILE_FIELDS.forEach(([k]) => { body[k] = form[k] == null ? '' : form[k]; });
      Object.assign(body, {
        billing_currency: form.billing_currency || 'INR',
        payment_terms_days: Number(form.payment_terms_days) || 30,
        is_registered_under_gst: !!form.is_registered_under_gst,
      });
      const r = await sendJson(`${BASE}/profile`, 'PUT', body);
      if (!r.ok) { alert((r.data && r.data.detail) || 'Could not save profile'); return; }
      alert('Seller profile saved');
    } finally { setBusy(false); }
  };
  if (!loaded) return <div className="p-6 text-gray-400">Loading…</div>;
  return (
    <div className="p-6">
      <h2 className="text-lg font-semibold text-gray-900">Seller profile</h2>
      <p className="text-sm text-gray-500 mb-4">Your company details as the seller on invoices you raise to your customers. Needed before you can generate invoices.</p>
      <div className="grid grid-cols-3 gap-4">
        {PROFILE_FIELDS.map(([k, label]) => (
          <Field key={k} label={label}>
            <input value={form[k] == null ? '' : form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </Field>
        ))}
        <Field label="Invoice currency">
          <select value={form.billing_currency || 'INR'} onChange={(e) => setForm({ ...form, billing_currency: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
            <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option><option>AED</option>
          </select>
        </Field>
        <Field label="Payment terms (days)">
          <input type="number" value={form.payment_terms_days || 30} onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
          <input type="checkbox" checked={!!form.is_registered_under_gst} onChange={(e) => setForm({ ...form, is_registered_under_gst: e.target.checked })} className="w-4 h-4" />
          Registered under GST
        </label>
      </div>
      <div className="flex justify-end mt-6">
        <button onClick={save} disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Save profile</button>
      </div>
    </div>
  );
};

// =========================================================================
// Shared little building blocks
// =========================================================================
const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

const FormButtons = ({ onCancel, onSave, busy, saveLabel = 'Save' }) => (
  <div className="flex justify-end gap-3 pt-4 border-t">
    <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
    <button type="button" onClick={onSave} disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50">
      <Save className="w-4 h-4" /> {saveLabel}
    </button>
  </div>
);

const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className={`bg-white rounded-lg shadow-xl w-full mx-4 max-h-[90vh] overflow-y-auto ${wide ? 'max-w-4xl' : 'max-w-2xl'}`}>
      <div className="border-b px-6 py-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

export default ServiceManagement;
